import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import { Activity, BarChart3, Users, HardHat, Hand, Wifi } from "lucide-react";

export default function FleetSidebar({ driverStats, onReviewDriver }) {
  const { session, hubData, onlineUsers } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const { drivers, requests } = hubData;

  const done = requests.filter(r => r.status === "DONE");
  const globalStats = {
    totalLiters: done.reduce((s, r) => s + (r.delivered_diesel || 0), 0),
    totalMachines: done.length,
    activeDrivers: drivers.filter(d => d.status === "ACTIVE").length,
    newRequests: requests.filter(r => r.status === "NEW").length,
  };

  // Check if a user is online via WebSocket presence
  const isOnline = (driverName) => {
    return (onlineUsers || []).some(u =>
      u.user_id === driverName ||
      u.user_id?.toLowerCase() === driverName?.toLowerCase()
    );
  };

  const onlineCount = (onlineUsers || []).length;

  return (
    <aside className="hidden lg:flex flex-col space-y-5 overflow-y-auto max-h-[calc(100vh-160px)] pr-1">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 px-1">
        <Activity className="w-4 h-4 text-lime-500" /> {t("fleet_intelligence")}
        {onlineCount > 0 && (
          <span className="ml-auto flex items-center gap-1 text-[9px] font-bold text-lime-500" data-testid="online-count">
            <Wifi className="w-3 h-3" /> {onlineCount} live
          </span>
        )}
      </div>

      {/* Global Stats */}
      <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-lime-50 rounded-xl"><BarChart3 className="w-4 h-4 text-lime-500" /></div>
          <div>
            <span className="text-[10px] font-bold text-lime-500 uppercase tracking-wider block">{t("fleet_volume")}</span>
            <span className="text-xl font-black font-mono text-slate-900">{globalStats.totalLiters}L</span>
          </div>
        </div>
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <span className="flex items-center gap-1"><Users className="w-3 h-3 text-lime-500" /> {globalStats.activeDrivers} {t("active_drivers")}</span>
          <span className="flex items-center gap-1"><HardHat className="w-3 h-3 text-amber-500" /> {globalStats.totalMachines} {t("refuelings")}</span>
        </div>
        {globalStats.newRequests > 0 && (
          <div className="p-2 bg-lime-50 border border-lime-500/20 rounded text-center">
            <span className="text-xs font-bold text-lime-600">{globalStats.newRequests} {t("new_requests")}</span>
          </div>
        )}
      </div>

      {/* Driver Cards */}
      {drivers.map(driver => {
        const stats = driverStats[driver.driver_name] || { liters: 0, machines: 0, sites: 0 };
        const isActive = driver.status === "ACTIVE";
        const live = isOnline(driver.driver_name);
        return (
          <div key={driver.driver_name} data-testid={`driver-card-${driver.driver_name}`}
            onClick={() => onReviewDriver(driver)}
            className="bg-slate-50 border border-slate-200 rounded-xl p-5 cursor-pointer hover:border-slate-300 transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-lg ${isActive ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                    {driver.driver_name[0]}
                  </div>
                  {/* Live presence indicator */}
                  {live && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5" data-testid={`presence-dot-${driver.driver_name}`}>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-lime-500 border-2 border-white" />
                    </span>
                  )}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{driver.driver_name}</h4>
                  <div className="flex items-center gap-2 mt-0.5">
                    {live ? (
                      <span className="text-[10px] font-bold text-lime-500 uppercase tracking-wider flex items-center gap-1">
                        <Wifi className="w-2.5 h-2.5" /> Online
                      </span>
                    ) : (
                      <div className={`status-dot ${isActive ? "online" : "offline"}`} />
                    )}
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{driver.region}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 text-amber-500">
                <Hand className="w-3.5 h-3.5" />
                <span className="text-xs font-black">{driver.high_five_count || 0}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="p-2 bg-white rounded text-center">
                <span className="text-[9px] font-bold text-slate-400 uppercase block">{ac.itemA}</span>
                <span className="text-sm font-black text-slate-900 font-mono">{stats.liters}{ac.itemAUnit}</span>
              </div>
              <div className="p-2 bg-white rounded text-center">
                <span className="text-[9px] font-bold text-slate-400 uppercase block">{t("machines")}</span>
                <span className="text-sm font-black text-slate-900 font-mono">{stats.machines}</span>
              </div>
            </div>
            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
              <button onClick={() => onReviewDriver(driver)}
                className="flex-1 py-2 bg-white text-slate-400 rounded text-[10px] font-bold uppercase tracking-wider hover:bg-slate-100 flex items-center justify-center gap-1 transition-all">
                <Activity className="w-3 h-3 text-lime-500" /> {t("analyze")}
              </button>
              <button onClick={async (e) => {
                  e.stopPropagation();
                  const apiMod = await import("@/lib/api");
                  await apiMod.default.sendHighFive({ from_user: session.userName || "Planner", to_user: driver.driver_name, hub_id: session.hubId });
                }}
                className="p-2 bg-amber-50 text-amber-500 rounded hover:bg-amber-500 hover:text-slate-950 transition-all active:scale-90" data-testid={`highfive-${driver.driver_name}`}>
                <Hand className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
    </aside>
  );
}
